/**
 * Payment & Subscription types for Speak Client.
 */

import { TrialTier, SubscriptionStatus } from "@speakai/shared";
import type { AWS_BUCKETS } from "@/config/aws-regions";
export { TrialTier, SubscriptionStatus };

// Stripe card minimum charge floor (USD).
export const STRIPE_CARD_MINIMUM_USD = 0.5;

// Floor matches the lowest preset so the minimum is always a selectable, default-selected tier.
export const ADD_BALANCE_MIN_USD = 10;
export const ADD_BALANCE_MAX_USD = 10_000;
export const ADD_BALANCE_DEFAULT_USD = 30;

// Web shows all 5; mobile filters to 3.
export const ADD_BALANCE_TIERS_WEB = [100, 50, 30, 15, 10] as const;
export const ADD_BALANCE_TIERS_MOBILE = [100, 30, 10] as const;

export enum TransactionType {
  Card = "card",
  Balance = "balance",
}

export enum TransactionSourceType {
  MediaType = "mediaType",
  ReloadBalance = "reload-balance",
  Transcription = "transcription",
  Translation = "translation",
  Subscription = "subscription",
}

export enum TransactionSourceName {
  Translation = "translation",
  HumanTranscription = "human-transcription",
  ReloadBalance = "reload-balance",
  Upload = "upload",
  Subscription = "subscription",
}

export enum PaymentSourceType {
  Stripe = "stripe",
  Paddle = "paddle",
  Revenuecat = "revenuecat",
  // Derived client-side, never returned by the server. Indicates an invoice/contract account
  // (planId === Speak_Custom with no active card-on-file rail). Do not pass to server endpoints
  // that expect a real rail (listSources, getInvoices, etc.) — those still want stripe/paddle/revenuecat.
  Direct_Deposit = "direct-deposit",
}

export enum SubscriptionPlanId {
  Speak_Free_Trial = "speak_free_trial",
  Speak_Pay_As_You_Go = "speak_pay_as_you_go",
  Speak_Custom = "speak_custom",
  Speak_Build_Custom_Plan = "speak_build_custom_plan",
}

import { OfferIds } from "@/config/billing-constants";
export { OfferIds };

export enum PremiumAddOnIds {
  PREMIUM_EXPORT = "premiumExport",
  CUSTOM_INSIGHTS = "customInsights",
  RECORDER_CUSTOMIZATION = "recorderCustomization",
  BASIC_MEETING_ASSISTANT = "basicMeetingAssistant",
  MEETING_ASSISTANT_CUSTOMIZATION = "meetingAssistantCustomization",
  INDIVIDUAL_MEDIA_SHARE = "individualMediaShare",
  LIBRARY_SHARE = "libraryShare",
  API_ACCESS = "apiAccess",
  CSV_UPLOAD = "csvUpload",
  FOLDER_CHAT = "folderChat",
}

export enum SupportAddOnIds {
  SPECIAL_OFFERS = "specialOffers",
  BULK_ORDER = "bulkOrder",
  WHITE_LABELING = "whiteLabeling",
}

export interface SourceDetails {
  totalAmount: number;
  sourceId: string;
  sourceType: TransactionSourceType;
  sourceName: TransactionSourceName;
  transactionType: TransactionType;
  description: string;
}

export interface PaddleTransaction {
  id: string;
  status: string;
  source: "paddle";
  details: {
    totals: {
      currencyCode: string;
      grandTotal: string;
    };
  };
  createdAt: string;
  billedAt: string;
  items: Array<{
    price: {
      description: string;
    };
  }>;
}

/** A Stripe charge from /subscription/transactions; only the fields the transactions list reads. */
export interface StripeCharge {
  id: string;
  created: number;
  amount: number;
  currency: string;
  status: string;
  description: string | null;
  receipt_url: string | null;
  failure_message: string | null;
  invoice?: string | null;
  statement_descriptor?: string | null;
}

export interface UserSubscription {
  billEndDate: string;
  billStartDate: string;
  durationId: string;
  features: SubscriptionFeatures;
  status: SubscriptionStatus;
  freeMinutes: number;
  freeNotes: number;
  freeCharacters: number;
  isExpired: boolean;
  isFree: boolean;
  name: string;
  planId: SubscriptionPlanId;
  offerId: OfferIds;
  paymentSource: PaymentSourceType;
  referralId: string;
  remainingDays?: number;
  remainingSeconds: number;
  startDate: string;
  usedMinutes: number;
  totalUsedMinutes: number;
  totalMedia?: number;
  notes: {
    totalNotes: number;
    remainingNotes: number;
  };
  currency: string;
  isPaymentMethod: boolean;
  isPricing: boolean;
  cost: {
    insights: number;
    prompts: number;
    humanTranscription: number;
    monthly: number;
    texts: number;
    total: number;
    discountedTotal: number;
    dubbing: number;
    translation: number;
  };
  invoiceLink: string;
  isEnterprise?: boolean;

  creditMode?: boolean;
  creditsGranted?: number;
  creditsUsed?: number;
  /** Grant left this cycle (granted − used), wallet NOT included. "Subscription credits". */
  creditsGrantRemaining?: number;
  /** Total spendable = grant remaining + wallet (from the balance endpoint). Affordability gate only. */
  creditsRemaining?: number;
  creditsResetOn?: string;
  creditsByType?: Partial<Record<"transcription" | "translation" | "chat" | "meeting" | "text", number>>;

  /**
   * S3 bucket id assigned to this company for media uploads. Populated server-side
   * from `Company.subscription.bucketId` (defaults to `SPEAKAI_STORAGE_US_EAST_1`
   * when the company has no override). Consumed by features/upload/cognito-upload.ts
   * to route uploads to the correct region. Region routing metadata — NOT PII.
   */
  bucketId?: AWS_BUCKETS | string;

  trialTier?: TrialTier;
  bumpAppliedFor?: string[];

  chatChars?: number;
  translationChars?: number;
  fileCount?: number;
  meetingCount?: number;
}

export interface SubscriptionFeatures {
  collaboration: {
    adminControl: boolean;
    allowUsers: number;
    assignStatus: boolean;
    groupTeamMember: boolean;
    teamStatistics: boolean;
  };
  embed: {
    analytics: boolean;
    brandCustomization: boolean;
    globalBrandCustomization: boolean;
    isFooter: boolean;
    library: boolean;
    player: boolean;
    watermark: boolean;
    isPlayerDomain: boolean;
    isLibraryDomain: boolean;
  };
  developers: {
    apiKeys: boolean;
    webhooks: boolean;
  };
  export: {
    bulk: boolean;
    premium: boolean;
    csv: boolean;
    html: boolean;
    json: boolean;
    pdf: boolean;
    redaction: boolean;
    vtt: boolean;
    word: boolean;
  };
  import: {
    bulk: boolean;
    csv: boolean;
    fileSizeInGB: number;
  };
  insights: {
    edit: boolean;
    custom: boolean;
  };
  texts: {
    csv: number;
    notes: number;
  };
  recorder: {
    brandCustomization: boolean;
    download: boolean;
    isFooter: boolean;
    isRecorderDomain: boolean;
  };
  support: {
    liveChat: boolean;
    phone: boolean;
    training: boolean;
  };
  transcript: {
    edit: boolean;
    totalMinutes: number;
  };
  translation: {
    dubbingMinutes: number;
    totalCharacters: number;
  };
  prompt: {
    isEmbeddings: boolean;
    totalCharacters: number;
    folderChat: boolean;
    mediaChat: boolean;
  };
  whiteLabel: {
    hosting: boolean;
  };
  meetingAssistant: {
    isActive: boolean;
    customization: boolean;
  };
  delete: {
    isCustomize: boolean;
    mediaInDays: number;
  };
  liveTranscription: {
    isEnabled: boolean;
    reprocessFile: boolean;
  };
  liveTranslation: {
    isEnabled: boolean;
  };
}

export interface PlanDurations {
  id: string;
  value: string;
  months: number;
  discountValue: number;
  label: string;
  rawLabel: string;
}

export interface PlanTypes {
  planId: SubscriptionPlanId;
  /** Server offer ID (e.g. "speak-starter"). Required for subscription creation. */
  offerId?: string;
  /** Offer features — surface to /subscription/stripe as total* fields so server provisions correct allowances. */
  features?: OfferFeatures;
  name: string;
  displayName: string;
  description: string;
  includedTitle: string;
  included: { key: string; params?: Record<string, unknown> }[];
  totalCost: number;
  discountedCost: number;
  discountedPercentage: number;
  saving: number;
}

export interface AddOnListInterface {
  id: PremiumAddOnIds | SupportAddOnIds;
  name: string;
  description: string;
  price: number;
  discountedPrice: number;
  isChecked: boolean;
  isContactSales: boolean;
  link: string;
}

export interface PaymentMethod {
  id: string;
  type: "card" | "link" | "paddle";
  isDefault: boolean;
  // Card-only (type === "card")
  brand?: string;
  last4?: string;
  expMonth?: number;
  expYear?: number;
  // Email-only (type === "link" or "paddle")
  email?: string;
}

export interface Invoice {
  id: string;
  amount_due: number;
  amount_paid: number;
  currency: string;
  status: string;
  created: number;
  hosted_invoice_url: string | null;
  lines: {
    data: Array<{
      period: {
        start: number;
        end: number;
      };
    }>;
  };
  total: number;
}

export interface BalanceResponse {
  balance: number;
}

export interface AutoReloadConfig {
  isActive: boolean;
  reloadAmount: number;
  minAmount: number;
  sourceId?: string;
}

export interface CreateSubscriptionData {
  title?: string;
  planId: SubscriptionPlanId;
  durationId: string;
  sourceId: string;
  totalHours: number;
  totalTeamMembers: number;
  totalTextNotes: number;
  totalTranslationCharacters: number;
  totalPromptCharacters: number;
  addOnList: AddOnListInterface[];
  /** Server offer ID (e.g. "speak-starter" / "speak-pro"). Variable across cohorts — keep as string, not the OfferIds enum. */
  offerId: string;
  sourceType: PaymentSourceType;
  totalCost: number;
  discountedCost: number;
  coupon: {
    id: string;
    code: string;
    percentOff: number;
    amountOff: number;
    originalCost: number;
  };
}

export interface OfferPricing {
  monthlyPrice: number;
  annualPrice: number;
  originalPrice: number;
  savingsAmount: number;
  discountPercentage: number;
  // absent = both cadences offered (back-compat)
  billingCadences?: ("monthly" | "yearly")[];
}

export interface OfferFeatures {
  transcriptionHours: number;
  chatCharacters: number;
  translationCharacters: number;
  textNotes: number;
  teamMembers: number;
  premiumAddOns: string[];
  fileSizeInGB: number;
}

export interface BalanceMatchData {
  revenueAmount: number;
  matchAmount: number;
}

export interface CreditCapabilities {
  transcriptionHours: number;
  meetingHours: number;
  translationCharacters: number;
  aiChats: number;
}

export interface Offer {
  offerId: OfferIds;
  name: string;
  title: string;
  description?: string;
  isActive: boolean;
  pricing: OfferPricing;
  features: OfferFeatures;
  offerType?: "subscription" | "balance_match";
  endDate?: string;
  balanceMatch?: BalanceMatchData;
  credits?: number;
  creditValue?: CreditCapabilities;
  creditMode?: boolean;
}

export interface SpecialOffer {
  offerId: string;
  name: string;
  title: string;
  description: string;
  features: {
    transcriptionHours: number;
    teamMembers: number;
    chatCharacters: number;
    textNotes: number;
    translationCharacters: number;
    fileSizeInGB: number;
    premiumAddOns: string[];
  };
  pricing: {
    monthlyPrice: number;
    annualPrice: number;
    originalPrice: number;
    savingsAmount: number;
    discountPercentage: number;
  };
  listItems: Array<{
    text: string;
    type: string;
    value?: string;
    params?: Record<string, unknown>;
  }>;
}
